import * as React from "react";
import { format } from "date-fns";
import { ChevronUp, ChevronDown } from "lucide-react";

import { Button } from "@/v2/components/ui/button";
import { Input } from "@/v2/components/ui/input";
import { Label } from "@/v2/components/ui/label";
import { Checkbox } from "@/v2/components/ui/checkbox";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { ComboBox } from "@/v2/components/ui/combobox";
import {
	renderTaskItem,
	renderActivityItem,
	renderMatterItem,
	renderSelectedMatter,
} from "@/v2/components/entry-form/pickerItems";
import {
	useMattersCache,
	useTasksCache,
	useActivitiesCache,
	useSettingsCache,
} from "@/shared/cache";
import type { Matter, Task, Activity } from "@/shared/cache";
import { incrementForMinimumUnits } from "@/shared/utils/units";
import type { SearchableField } from "@/shared/utils/smartSearch";
import { parseEntryDate } from "@/v2/lib/dates";
import {
	distinctValues,
	hasMixedEmptyAndFilled,
	unitsCandidates,
} from "@/v2/lib/reconcileSources";
import {
	isZurichMatter,
	snapUnitsForMatter,
	stepUnitsUp,
	stepUnitsDown,
} from "@/shared/utils/zurich";
import {
	availableTasksForMatter,
	availableActivitiesForMatter,
} from "@/shared/utils/firmCodes";
import { isTaskRequired, isActivityRequired } from "@/shared/utils/firm";
import type { Entry } from "@/shared/types/entries";
import { PresetChips, type PresetChip } from "./PresetChips";

const NONE_CHIP = "__none__";

export interface ActionFormSubmit {
	date: Date;
	matterId: string | null;
	taskId: string | null;
	activityId: string | null;
	unitsSeconds: number;
	includeUnits?: boolean;
}

export interface ActionFormProps {
	formId: string;
	entries: Entry[];
	/** Field-conflicts overlaid from a server FIELD_CONFLICT response. */
	conflicts?: { field: string; values: string[] }[];
	/** When true, render the includeUnits checkbox (group only). */
	showIncludeUnits?: boolean;
	/** Initial value for includeUnits when shown. */
	defaultIncludeUnits?: boolean;
	/**
	 * Render the "sum units" / "sum seconds" chip row when the two strategies
	 * yield different billable totals. Merge + collapse expose this; group
	 * does not (the backend has no `unitsAggregation` for group — it always
	 * sums per-entry units).
	 */
	showUnitsAggregation?: boolean;
	/**
	 * Collapse mode. When set, the inheritable fields (date / matter / task /
	 * activity) re-seed from the chosen main entry whenever it changes. The
	 * picker UI itself is rendered separately by the host (CollapseDialog) so
	 * the layout can place it wherever it likes — e.g. as a side panel.
	 * Cross-source chips remain available for explicit override.
	 */
	mainSourceId?: number;
	onSubmit: (values: ActionFormSubmit) => void;
}

function formatDateForApi(d: Date): string {
	const month = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	const year = d.getFullYear();
	return `${month}/${day}/${year}`;
}

function secondsToUnitsDisplay(seconds: number): string {
	const units = seconds / 3600;
	if (Math.abs(units * 10 - Math.round(units * 10)) < 1e-6) {
		return (Math.round(units * 10) / 10).toFixed(1);
	}
	return units.toFixed(2);
}

export function ActionForm({
	formId,
	entries,
	conflicts,
	showIncludeUnits = false,
	defaultIncludeUnits = true,
	showUnitsAggregation = false,
	mainSourceId,
	onSubmit,
}: ActionFormProps) {
	const isCollapse = mainSourceId !== undefined;
	const mainEntry = React.useMemo(
		() =>
			isCollapse ? (entries.find((e) => e.id === mainSourceId) ?? null) : null,
		[isCollapse, mainSourceId, entries],
	);
	const { matters, isLoading: mattersLoading } = useMattersCache();
	const { tasks } = useTasksCache();
	const { activities } = useActivitiesCache();
	const { settings } = useSettingsCache();
	const increment = incrementForMinimumUnits(settings.minimum_units);

	const conflictFields = React.useMemo(() => {
		const set = new Set<string>();
		for (const c of conflicts ?? []) set.add(c.field);
		return set;
	}, [conflicts]);

	const dateDistinct = React.useMemo(
		() => distinctValues(entries, "date"),
		[entries],
	);
	const matterDistinct = React.useMemo(
		() => distinctValues(entries, "matter_id"),
		[entries],
	);
	const taskDistinct = React.useMemo(
		() => distinctValues(entries, "task"),
		[entries],
	);
	const activityDistinct = React.useMemo(
		() => distinctValues(entries, "activity"),
		[entries],
	);

	const initialDate = React.useMemo(() => {
		if (isCollapse && mainEntry?.date) return parseEntryDate(mainEntry.date);
		if (dateDistinct.length === 1)
			return parseEntryDate(dateDistinct[0] as string);
		if (entries[0]?.date) return parseEntryDate(entries[0].date);
		return new Date();
	}, [isCollapse, mainEntry, dateDistinct, entries]);

	const initialMatter = React.useMemo(() => {
		if (isCollapse)
			return mainEntry?.matter_id ? String(mainEntry.matter_id) : "";
		return matterDistinct.length === 1 &&
			!hasMixedEmptyAndFilled(entries, "matter_id")
			? String(matterDistinct[0])
			: "";
	}, [isCollapse, mainEntry, matterDistinct, entries]);

	const initialTask = React.useMemo(() => {
		if (isCollapse) return mainEntry?.task ? String(mainEntry.task) : "";
		return taskDistinct.length === 1 && !hasMixedEmptyAndFilled(entries, "task")
			? String(taskDistinct[0])
			: "";
	}, [isCollapse, mainEntry, taskDistinct, entries]);

	const initialActivity = React.useMemo(() => {
		if (isCollapse)
			return mainEntry?.activity ? String(mainEntry.activity) : "";
		return activityDistinct.length === 1 &&
			!hasMixedEmptyAndFilled(entries, "activity")
			? String(activityDistinct[0])
			: "";
	}, [isCollapse, mainEntry, activityDistinct, entries]);

	const [date, setDate] = React.useState<Date>(initialDate);
	const [matterId, setMatterId] = React.useState<string>(initialMatter);
	const [task, setTask] = React.useState<string>(initialTask);
	const [activity, setActivity] = React.useState<string>(initialActivity);

	// Resolve the selected matter's client now so `candidates` (units chip
	// preview) can apply the matter-aware Zurich rule. Declared between the
	// matter state and the units state so the units initial value is already
	// matter-snapped on first render.
	const selectedMatter = React.useMemo(
		() => matters.find((m) => m.matter_id === matterId) ?? null,
		[matters, matterId],
	);
	const selectedClient = selectedMatter?.client ?? "";

	// Firm-rule-driven required flags. Default to `true` while matters are
	// still hydrating so the picker doesn't briefly show "not required" before
	// settling. Mirrors EntryForm so action dialogs enforce the same contract
	// as add/edit.
	const taskRequired = React.useMemo(
		() => (mattersLoading ? true : isTaskRequired(matterId, matters)),
		[matterId, matters, mattersLoading],
	);
	const activityRequired = React.useMemo(
		() => (mattersLoading ? true : isActivityRequired(matterId, matters)),
		[matterId, matters, mattersLoading],
	);

	const candidates = React.useMemo(
		() => unitsCandidates(entries, increment),
		[entries, increment],
	);
	const showUnitsChips =
		showUnitsAggregation &&
		candidates.sumUnits.units !== candidates.sumSeconds.units;

	const [unitsSeconds, setUnitsSeconds] = React.useState<number>(
		candidates.sumUnits.snappedSeconds,
	);
	const [includeUnits, setIncludeUnits] =
		React.useState<boolean>(defaultIncludeUnits);

	// Field errors set by submit / open validation. Cleared individually when
	// the user changes the corresponding field.
	const [errors, setErrors] = React.useState<{
		task?: string;
		activity?: string;
	}>({});

	// Re-snap when the user changes matters mid-dialog. Example: a 0.05 Zurich
	// value becomes invalid on a non-Zurich matter and must ceil to 0.1.
	// `setState` with `===` is idempotent, so the no-change case is a no-op.
	React.useEffect(() => {
		setUnitsSeconds((prev) => {
			const snapped = snapUnitsForMatter(prev / 3600, selectedClient);
			return Math.round(snapped * 3600);
		});
	}, [selectedClient]);

	// Clear task/activity when the chosen matter's firm rules don't require
	// them — guard on `matterId` so clearing the matter doesn't wipe values
	// the user typed intentionally (null-matter mode keeps the pickers open).
	// Every firm follows the same applicability rule as EntryForm.
	React.useEffect(() => {
		if (!matterId) return;
		if (!taskRequired && task) {
			setTask("");
			setActiveTaskChip(null);
		}
		if (!activityRequired && activity) {
			setActivity("");
			setActiveActivityChip(null);
		}
		// Also drop any stale required-error for fields the new matter has
		// turned into "not required" — the field is about to be disabled, so
		// the user shouldn't see an unfixable error.
		setErrors((e) => ({
			task: !taskRequired ? undefined : e.task,
			activity: !activityRequired ? undefined : e.activity,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [matterId, taskRequired, activityRequired]);

	const [activeDateChip, setActiveDateChip] = React.useState<string | null>(
		() => {
			if (isCollapse) return mainEntry?.date ? String(mainEntry.date) : null;
			return dateDistinct.length === 1 ? String(dateDistinct[0]) : null;
		},
	);
	const [activeMatterChip, setActiveMatterChip] = React.useState<string | null>(
		() => {
			if (isCollapse)
				return mainEntry?.matter_id ? String(mainEntry.matter_id) : null;
			return matterDistinct.length === 1 &&
				!hasMixedEmptyAndFilled(entries, "matter_id")
				? String(matterDistinct[0])
				: null;
		},
	);
	const [activeTaskChip, setActiveTaskChip] = React.useState<string | null>(
		() => {
			if (isCollapse) return mainEntry?.task ? String(mainEntry.task) : null;
			return taskDistinct.length === 1 &&
				!hasMixedEmptyAndFilled(entries, "task")
				? String(taskDistinct[0])
				: null;
		},
	);
	const [activeActivityChip, setActiveActivityChip] = React.useState<
		string | null
	>(() => {
		if (isCollapse)
			return mainEntry?.activity ? String(mainEntry.activity) : null;
		return activityDistinct.length === 1 &&
			!hasMixedEmptyAndFilled(entries, "activity")
			? String(activityDistinct[0])
			: null;
	});
	const [activeUnitsChip, setActiveUnitsChip] = React.useState<string | null>(
		"units",
	);

	// Collapse: when the user picks a different main source, re-anchor the
	// inheritable fields to the new main's values. Cross-source chips remain
	// available for explicit override.
	React.useEffect(() => {
		if (!isCollapse || !mainEntry) return;
		setDate(parseEntryDate(mainEntry.date));
		setActiveDateChip(mainEntry.date ? String(mainEntry.date) : null);
		const mainMatter = mainEntry.matter_id ? String(mainEntry.matter_id) : "";
		setMatterId(mainMatter);
		setActiveMatterChip(mainMatter || null);
		const mainTask = mainEntry.task ? String(mainEntry.task) : "";
		setTask(mainTask);
		setActiveTaskChip(mainTask || null);
		const mainActivity = mainEntry.activity ? String(mainEntry.activity) : "";
		setActivity(mainActivity);
		setActiveActivityChip(mainActivity || null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mainSourceId]);

	const dateNeedsPick = React.useMemo(() => {
		return dateDistinct.length > 1 || conflictFields.has("date");
	}, [dateDistinct.length, conflictFields]);

	const matterNeedsPick = React.useMemo(() => {
		return (
			matterDistinct.length > 1 ||
			(matterDistinct.length === 1 &&
				hasMixedEmptyAndFilled(entries, "matter_id")) ||
			conflictFields.has("matterId")
		);
	}, [matterDistinct, entries, conflictFields]);

	// Picker disabled when a matter is selected AND the firm's rules say the
	// code isn't required. Null-matter keeps both pickers enabled (the user
	// might still want to pick optionally). isActivityRequired includes the
	// Naman Howell and Gould Ratner rules based on task_group.
	const taskDisabled = !!matterId && !taskRequired;
	const activityDisabled = !!matterId && !activityRequired;

	// When a field is disabled, suppress the chip row even if sources happen to
	// disagree — the user can't pick anyway, and the value will be stripped on
	// submit.
	const taskNeedsPick = React.useMemo(() => {
		if (taskDisabled) return false;
		return (
			taskDistinct.length > 1 ||
			(taskDistinct.length === 1 && hasMixedEmptyAndFilled(entries, "task")) ||
			conflictFields.has("taskId")
		);
	}, [taskDisabled, taskDistinct, entries, conflictFields]);

	const activityNeedsPick = React.useMemo(() => {
		if (activityDisabled) return false;
		return (
			activityDistinct.length > 1 ||
			(activityDistinct.length === 1 &&
				hasMixedEmptyAndFilled(entries, "activity")) ||
			conflictFields.has("activityId")
		);
	}, [activityDisabled, activityDistinct, entries, conflictFields]);

	// FIELD_CONFLICT overlay: clear the conflicting input and chip on (re)open.
	const conflictKey = React.useMemo(
		() => JSON.stringify(conflicts ?? []),
		[conflicts],
	);
	React.useEffect(() => {
		if (!conflicts || conflicts.length === 0) return;
		for (const c of conflicts) {
			if (c.field === "date") {
				setDate(new Date());
				setActiveDateChip(null);
			}
			if (c.field === "matterId") {
				setMatterId("");
				setActiveMatterChip(null);
			}
			if (c.field === "taskId") {
				setTask("");
				setActiveTaskChip(null);
			}
			if (c.field === "activityId") {
				setActivity("");
				setActiveActivityChip(null);
			}
		}
		// Drop any client-side required errors — the conflict overlay re-seeds
		// fields from the server's payload; the user picks fresh values.
		setErrors({});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [conflictKey]);

	// One-shot validate-on-open: once matters finish hydrating, surface
	// required-but-missing errors immediately so the user can see them before
	// clicking submit. Deferred until `!mattersLoading` so taskRequired /
	// activityRequired reflect settled data.
	const hasValidatedOnOpen = React.useRef(false);
	React.useEffect(() => {
		if (mattersLoading || hasValidatedOnOpen.current) return;
		hasValidatedOnOpen.current = true;
		const next: { task?: string; activity?: string } = {};
		if (taskRequired && !task) next.task = "Task is required";
		if (activityRequired && !activity) next.activity = "Activity is required";
		if (next.task || next.activity) setErrors(next);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mattersLoading]);

	// Chip ordering: ascending across the board. Dates by chronological day;
	// codes by label/code alphabetically. The "None" pseudo-chip — when the
	// sources mix empty + filled values — always pins to the end so it doesn't
	// shuffle around with the real options.
	const dateChips: PresetChip[] = React.useMemo(() => {
		const values = new Set<string>(dateDistinct.map((v) => String(v)));
		const fromServer = conflicts?.find((c) => c.field === "date")?.values ?? [];
		for (const v of fromServer) values.add(v);
		return Array.from(values)
			.map((v) => ({ value: v, date: parseEntryDate(v) }))
			.sort((a, b) => a.date.getTime() - b.date.getTime())
			.map(({ value, date }) => ({ value, label: format(date, "MMM d") }));
	}, [dateDistinct, conflicts]);

	const matterChipBase: PresetChip[] = React.useMemo(() => {
		const values = new Set<string>(matterDistinct.map((v) => String(v)));
		const fromServer =
			conflicts?.find((c) => c.field === "matterId")?.values ?? [];
		for (const v of fromServer) values.add(v);
		const chips: PresetChip[] = Array.from(values)
			.map((id) => {
				const m = matters.find((mat) => mat.matter_id === id);
				return { value: id, label: m ? m.matter.trim() : id };
			})
			.sort((a, b) => a.label.localeCompare(b.label));
		if (hasMixedEmptyAndFilled(entries, "matter_id")) {
			chips.push({ value: NONE_CHIP, label: "None" });
		}
		return chips;
	}, [matterDistinct, conflicts, matters, entries]);

	const taskChips: PresetChip[] = React.useMemo(() => {
		const values = new Set<string>(taskDistinct.map((v) => String(v)));
		const fromServer =
			conflicts?.find((c) => c.field === "taskId")?.values ?? [];
		for (const v of fromServer) values.add(v);
		const chips: PresetChip[] = Array.from(values)
			.sort((a, b) => a.localeCompare(b))
			.map((code) => ({ value: code, label: code }));
		if (hasMixedEmptyAndFilled(entries, "task")) {
			chips.push({ value: NONE_CHIP, label: "None" });
		}
		return chips;
	}, [taskDistinct, conflicts, entries]);

	const activityChips: PresetChip[] = React.useMemo(() => {
		const values = new Set<string>(activityDistinct.map((v) => String(v)));
		const fromServer =
			conflicts?.find((c) => c.field === "activityId")?.values ?? [];
		for (const v of fromServer) values.add(v);
		const chips: PresetChip[] = Array.from(values)
			.sort((a, b) => a.localeCompare(b))
			.map((code) => ({ value: code, label: code }));
		if (hasMixedEmptyAndFilled(entries, "activity")) {
			chips.push({ value: NONE_CHIP, label: "None" });
		}
		return chips;
	}, [activityDistinct, conflicts, entries]);

	const unitsChips: PresetChip[] = React.useMemo(() => {
		if (!showUnitsChips) return [];
		const sumUnitsLabel = `${secondsToUnitsDisplay(candidates.sumUnits.snappedSeconds)} (sum units)`;
		const sumSecondsLabel = `${secondsToUnitsDisplay(candidates.sumSeconds.snappedSeconds)} (sum seconds)`;
		return [
			{ value: "units", label: sumUnitsLabel },
			{ value: "seconds", label: sumSecondsLabel },
		];
	}, [showUnitsChips, candidates]);

	// Duration display + editing
	const [durationInput, setDurationInput] = React.useState<string>(() =>
		secondsToUnitsDisplay(unitsSeconds),
	);
	React.useEffect(() => {
		setDurationInput(secondsToUnitsDisplay(unitsSeconds));
	}, [unitsSeconds]);

	const handleDurationChange = (raw: string) => {
		setDurationInput(raw);
		const parsed = parseFloat(raw);
		if (!Number.isNaN(parsed) && parsed >= 0) {
			setUnitsSeconds(Math.round(parsed * 3600));
			setActiveUnitsChip(null);
		}
	};
	const handleDurationBlur = () => {
		const parsed = parseFloat(durationInput);
		if (Number.isNaN(parsed) || parsed < 0) {
			setDurationInput(secondsToUnitsDisplay(unitsSeconds));
			return;
		}
		const snapped = snapUnitsForMatter(parsed, selectedClient);
		setUnitsSeconds(Math.round(snapped * 3600));
		setDurationInput(snapped.toFixed(snapped % 0.1 === 0 ? 1 : 2));
	};
	const handleDurationStepUp = () => {
		const current = unitsSeconds / 3600;
		const next = stepUnitsUp(current, selectedClient);
		setUnitsSeconds(Math.round(next * 3600));
		setActiveUnitsChip(null);
	};
	const handleDurationStepDown = () => {
		const current = unitsSeconds / 3600;
		const next = stepUnitsDown(current, selectedClient);
		setUnitsSeconds(Math.round(next * 3600));
		setActiveUnitsChip(null);
	};

	const matterFields: ((item: Matter) => SearchableField)[] = React.useMemo(
		() => [
			(m: Matter) => ({ value: m.matter, weight: 1.5 }),
			(m: Matter) => ({ value: m.client, weight: 1.2 }),
			(m: Matter) => ({ value: m.description, weight: 1.0 }),
		],
		[],
	);
	const taskFields: ((item: Task) => SearchableField)[] = React.useMemo(
		() => [
			(t: Task) => ({ value: t.task, weight: 1.5 }),
			(t: Task) => ({ value: t.task_description, weight: 1.0 }),
		],
		[],
	);
	const activityFields: ((item: Activity) => SearchableField)[] = React.useMemo(
		() => [
			(a: Activity) => ({ value: a.activity, weight: 1.5 }),
			(a: Activity) => ({ value: a.activity_description, weight: 1.0 }),
		],
		[],
	);

	const availableTasks = React.useMemo(
		() => availableTasksForMatter(tasks, matters, matterId),
		[tasks, matters, matterId],
	);

	React.useEffect(() => {
		if (!matterId || !task || tasks.length === 0) return;
		if (!availableTasks.some((t) => t.task === task)) {
			setTask("");
			setActiveTaskChip(null);
			setErrors((e) => ({ ...e, task: undefined }));
		}
	}, [matterId, task, tasks.length, availableTasks]);

	const uniqueTasks = React.useMemo(() => {
		const seen = new Set<string>();
		return availableTasks.filter((t) => {
			const key = `${t.task}-${t.task_description}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	}, [availableTasks]);

	const availableActivities = React.useMemo(
		() => availableActivitiesForMatter(activities, matters, matterId),
		[activities, matters, matterId],
	);

	React.useEffect(() => {
		if (!matterId || !activity || activities.length === 0) return;
		if (!availableActivities.some((a) => a.activity === activity)) {
			setActivity("");
			setActiveActivityChip(null);
			setErrors((e) => ({ ...e, activity: undefined }));
		}
	}, [matterId, activity, activities.length, availableActivities]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Required-field validation. Mirrors EntryForm's validateAndPrepare:
		// block on missing required, then strip task/activity values when a
		// matter IS selected and its firm rules say the code isn't required —
		// null-matter mode keeps both values intentional.
		const nextErrors: { task?: string; activity?: string } = {};
		if (taskRequired && !task) nextErrors.task = "Task is required";
		else if (
			matterId &&
			task &&
			tasks.length > 0 &&
			!availableTasks.some((t) => t.task === task)
		) {
			nextErrors.task = "Task is not available for this matter";
		}
		if (activityRequired && !activity)
			nextErrors.activity = "Activity is required";
		else if (
			matterId &&
			activity &&
			activities.length > 0 &&
			!availableActivities.some((a) => a.activity === activity)
		) {
			nextErrors.activity = "Activity is not available for this matter";
		}
		if (nextErrors.task || nextErrors.activity) {
			setErrors(nextErrors);
			return;
		}
		setErrors({});

		const submitTask = matterId && !taskRequired ? null : task ? task : null;
		const submitActivity =
			matterId && !activityRequired ? null : activity ? activity : null;

		onSubmit({
			date,
			matterId: matterId ? matterId : null,
			taskId: submitTask,
			activityId: submitActivity,
			unitsSeconds,
			...(showIncludeUnits ? { includeUnits } : {}),
		});
	};

	return (
		<form id={formId} onSubmit={handleSubmit} className="grid gap-4 p-4">
			{/* Matter */}
			<div className="grid w-full gap-1.5">
				<Label>Matter</Label>
				{matterNeedsPick && (
					<PresetChips
						chips={matterChipBase}
						activeValue={activeMatterChip}
						onPick={(value) => {
							if (value === null) {
								setActiveMatterChip(null);
								return;
							}
							setActiveMatterChip(value);
							if (value === NONE_CHIP) {
								setMatterId("");
							} else {
								setMatterId(value);
							}
						}}
					/>
				)}
				<ComboBox
					items={matters}
					value={matterId || null}
					onChange={(id) => {
						setMatterId(id);
						setActiveMatterChip(
							matterChipBase.some((c) => c.value === id) ? id : null,
						);
					}}
					fields={matterFields}
					getKey={(m) => m.matter_id}
					getDisplayLabel={(m) =>
						`${m.matter.trim()} | ${m.description.trim()} - ${m.client.trim()}`
					}
					getSearchValue={(m) => `${m.matter.trim()} ${m.description.trim()}`}
					renderItem={renderMatterItem}
					renderSelected={renderSelectedMatter}
					placeholder="Select matter..."
					clearable
					onClear={() => {
						setMatterId("");
						setActiveMatterChip(
							hasMixedEmptyAndFilled(entries, "matter_id") ? NONE_CHIP : null,
						);
					}}
				/>
			</div>

			{/* Task */}
			<div className="grid w-full gap-1.5">
				<Label>Task</Label>
				{taskNeedsPick && (
					<PresetChips
						chips={taskChips}
						activeValue={activeTaskChip}
						onPick={(value) => {
							if (value === null) {
								setActiveTaskChip(null);
								return;
							}
							setActiveTaskChip(value);
							setTask(value === NONE_CHIP ? "" : value);
							if (errors.task) setErrors((e) => ({ ...e, task: undefined }));
						}}
					/>
				)}
				<ComboBox
					items={uniqueTasks}
					value={(() => {
						if (!task) return null;
						const found = uniqueTasks.find((t) => t.task === task);
						return found ? `${found.task}-${found.task_description}` : null;
					})()}
					onChange={(key) => {
						const t = uniqueTasks.find(
							(it) => `${it.task}-${it.task_description}` === key,
						);
						const code = t?.task ?? "";
						setTask(code);
						setActiveTaskChip(
							taskChips.some((c) => c.value === code) ? code : null,
						);
						if (errors.task) setErrors((e) => ({ ...e, task: undefined }));
					}}
					fields={taskFields}
					getKey={(t) => `${t.task}-${t.task_description}`}
					getDisplayLabel={(t) => `${t.task} - ${t.task_description}`}
					getSearchValue={(t) => t.task}
					renderItem={renderTaskItem}
					renderSelected={renderTaskItem}
					placeholder="Select task..."
					disabled={taskDisabled}
					disabledMessage="Not required"
					error={errors.task}
					clearable
					onClear={() => {
						setTask("");
						setActiveTaskChip(
							hasMixedEmptyAndFilled(entries, "task") ? NONE_CHIP : null,
						);
					}}
				/>
			</div>

			{/* Activity */}
			<div className="grid w-full gap-1.5">
				<Label>Activity</Label>
				{activityNeedsPick && (
					<PresetChips
						chips={activityChips}
						activeValue={activeActivityChip}
						onPick={(value) => {
							if (value === null) {
								setActiveActivityChip(null);
								return;
							}
							setActiveActivityChip(value);
							setActivity(value === NONE_CHIP ? "" : value);
							if (errors.activity)
								setErrors((e) => ({ ...e, activity: undefined }));
						}}
					/>
				)}
				<ComboBox
					items={availableActivities}
					value={(() => {
						if (!activity) return null;
						const found = availableActivities.find(
							(a) => a.activity === activity,
						);
						return found
							? `${found.activity}-${found.activity_description}`
							: null;
					})()}
					onChange={(key) => {
						const a = availableActivities.find(
							(it) => `${it.activity}-${it.activity_description}` === key,
						);
						const code = a?.activity ?? "";
						setActivity(code);
						setActiveActivityChip(
							activityChips.some((c) => c.value === code) ? code : null,
						);
						if (errors.activity)
							setErrors((e) => ({ ...e, activity: undefined }));
					}}
					fields={activityFields}
					getKey={(a) => `${a.activity}-${a.activity_description}`}
					getDisplayLabel={(a) => `${a.activity} - ${a.activity_description}`}
					getSearchValue={(a) => a.activity}
					renderItem={renderActivityItem}
					renderSelected={renderActivityItem}
					placeholder="Select activity..."
					disabled={activityDisabled}
					disabledMessage="Not required"
					error={errors.activity}
					clearable
					onClear={() => {
						setActivity("");
						setActiveActivityChip(
							hasMixedEmptyAndFilled(entries, "activity") ? NONE_CHIP : null,
						);
					}}
				/>
			</div>

			{/* Date */}
			<div className="grid w-full gap-1.5">
				<Label>Date</Label>
				{dateNeedsPick && (
					<PresetChips
						chips={dateChips}
						activeValue={activeDateChip}
						onPick={(value) => {
							if (value === null) {
								setActiveDateChip(null);
								return;
							}
							setActiveDateChip(value);
							setDate(parseEntryDate(value));
						}}
					/>
				)}
				<DatePicker
					date={date}
					onDateChange={(day) => {
						setDate(day);
						const key = formatDateForApi(day);
						setActiveDateChip(
							dateChips.some((c) => c.value === key) ? key : null,
						);
					}}
					selectionMode="single"
				/>
			</div>

			{/* Units */}
			<div className="grid w-full gap-1.5">
				<Label>Units</Label>
				{showUnitsChips && (
					<PresetChips
						chips={unitsChips}
						activeValue={activeUnitsChip}
						onPick={(value) => {
							if (value === null) {
								setActiveUnitsChip(null);
								return;
							}
							setActiveUnitsChip(value);
							if (value === "units")
								setUnitsSeconds(candidates.sumUnits.snappedSeconds);
							else if (value === "seconds")
								setUnitsSeconds(candidates.sumSeconds.snappedSeconds);
						}}
					/>
				)}
				<div className="flex gap-1">
					<Input
						type="text"
						inputMode="decimal"
						autoComplete="off"
						value={durationInput}
						onChange={(e) => handleDurationChange(e.target.value)}
						onBlur={handleDurationBlur}
						className="text-foreground bg-card border-border"
					/>
					<div className="flex flex-col gap-0.5">
						<Button
							type="button"
							variant="outline"
							size="icon"
							onClick={handleDurationStepUp}
							className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
							tabIndex={-1}
						>
							<ChevronUp className="h-3 w-3" />
						</Button>
						<Button
							type="button"
							variant="outline"
							size="icon"
							onClick={handleDurationStepDown}
							className="h-4.5 w-7 p-0 text-foreground bg-card border-border hover:bg-muted"
							tabIndex={-1}
						>
							<ChevronDown className="h-3 w-3" />
						</Button>
					</div>
				</div>
				{selectedClient && isZurichMatter(selectedClient) && (
					<p className="text-[11px] text-muted-foreground">
						Zurich grid: 3-minute increments allowed.
					</p>
				)}
			</div>

			{/* Group-only: include unit segments toggle */}
			{showIncludeUnits && (
				<div className="grid gap-1.5">
					<div className="flex items-start gap-2">
						<Checkbox
							id={`${formId}-include-units`}
							checked={includeUnits}
							onCheckedChange={(v) => setIncludeUnits(!!v)}
						/>
						<div className="grid gap-0.5">
							<Label
								htmlFor={`${formId}-include-units`}
								className="cursor-pointer"
							>
								Show unit segments in narrative
							</Label>
							<p className="text-[11px] text-muted-foreground leading-snug">
								e.g. "Drafted motion (0.3); reviewed exhibits (0.1)"
							</p>
						</div>
					</div>
				</div>
			)}
		</form>
	);
}
